<template>
    <div id="app" :class="darkmode ? 'dark' : ''">
        <router-view />
    </div>
</template>

<script>
import { mapState } from 'vuex'
export default {
    name: 'App',
    data() {
        return {
            animated: false
        }
    },
    computed: {
        ...mapState('theme', ['darkmode'])
    },
    watch: {
        darkmode: function() {
            document.body.style.backgroundColor = this.darkmode ? '#0f1218' : '#f9fafb'
        }
    },
    mounted: function() {
        document.body.style.backgroundColor = this.darkmode ? '#0f1218' : '#f9fafb'
    }
}
</script>

<style>
:root {
    --bg-1: #f9fafb;
    --bg-2: #e6e9ed;
    --bg-3: #fff;
    --bg-4: #e6e9ed;
    --font-1: #3a3a3a;
    --font-2: #2a2a2a;
    --font-3: #949494;
    --font-4: #4c4c4c;
    --font-5: #676273;
    --accent-1: #4a3473;
    --accent-2: #562bbf;
    --accent-3: #26007b;
    --accent-4: #b95050;
    --link: #6724ff;
    --link-hover: #7e47ff;
    --accent-gradient: linear-gradient(130deg, #7339f9, #3808af);
    --accent-gradient-down: linear-gradient(360deg, #7339f9, #3808af);
    --accent-gradient-focus: linear-gradient(360deg, #7339f9, #3808af);
    --bm-color: black;
    --sidebar-border: #d8d8d8;
    --code-1: #5b2cc7;
    --code-2: #c7329d;
    --code-bg: rgb(255 255 255 / 90%);
    --code-desc: #605874;
    --code-3: #047108;
    --card-bg: white;
    --table-odd: #e8e8e8;
    --table-even: #ededed;
    --table-head: white;
    --top-bar-shadow: inset 0 4px 6px -1px rgb(0 0 0 / 10%), 0 2px 4px -1px rgb(0 0 0 / 6%);
}

.dark {
    --bg-1: #0f1218;
    --bg-2: hsl(220, 23%, 12%);
    --bg-3: hsl(220, 23%, 4%);
    --bg-4: hsl(220, 23%, 12%);
    --font-1: #dcdcdc;
    --font-2: #dadada;
    --font-3: rgb(146, 146, 146);
    --font-4: hsl(0, 0%, 30%);
    --font-5: hsl(258, 8%, 42%);
    --accent-1: #2b4fa1;
    --accent-2: hsl(220deg 62% 63%);
    --accent-3: #002a8d;
    --accent-4: #c98282;
    --link: #558ff9;
    --link-hover: #70a0fa;
    --accent-gradient: linear-gradient(130deg, hsl(220deg 71% 27%), hsl(220deg 54% 22%));
    --accent-gradient-down: linear-gradient(180deg, hsl(220deg 71% 27%), hsl(220deg 54% 22%));
    --accent-gradient-focus: linear-gradient(180deg, hsl(220deg 71% 27%), hsl(220deg 54% 22%));
    --bm-color: rgb(221, 221, 221);
    --sidebar-border: black;
    --code-1: #7898e3;
    --code-2: #baceff;
    --code-bg: #13151b;
    --code-desc: #ada6bf;
    --code-3: #bcedf1;
    --card-bg: #171921;
    --table-odd: #171a21;
    --table-even: #15171e;
    --table-head: white;
    --top-bar-shadow: 0 2px 2px -1px rgb(156 156 156 / 10%);
}

h1 {
    font-size: 2.05em;
}

h2 {
    font-size: 1.75em;
}

h3 {
    font-size: 1.55rem;
}

h4 {
    font-size: 1.15rem;
    color: var(--font-2);
}

a {
    color: var(--link);
}

a:hover {
    color: var(--link-hover);
}

#app {
    color: var(--font-1);
    height: 100%;
}

body {
    margin: 0;
    height: 100%;
    font-family: 'Inter', sans-serif;
    background-color: var(--bg-1);
}

.notifications {
    top: 0;
    position: fixed;
    left: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 5;
}

.bm-menu {
    background-color: var(--bg-4) !important;
    color: var(--font-2) !important;
    padding-top: 52px;
    transition: 0.3s !important;
}

.bm-item-list {
    margin-left: 0 !important;
    font-size: inherit !important;
}

.bm-burger-button {
    height: 48px !important;
    width: 48px !important;
    left: 2px !important;
    top: 36px !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
}

.bm-cross-button {
    height: 48px !important;
    width: 48px !important;
    position: absolute !important;
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
    right: 12px !important;
    top: 10px !important;
}

.dark .bm-cross {
    background: rgb(211, 211, 211) !important;
}

.line-style {
    display: none !important;
}

.line-style:nth-of-type(2) {
    display: block !important;

    left: calc(50% - 9px) !important;
    top: calc(50% - 1px) !important;
    width: 17px !important;
    height: 3px !important;
    background-color: var(--bm-color) !important;
}

.line-style:nth-of-type(2)::before {
    content: '' !important;
    position: absolute !important;
    width: 100% !important;
    height: 3px !important;
    top: -6px !important;
    left: 0 !important;
    background-color: var(--bm-color) !important;
}

.line-style:nth-of-type(2)::after {
    content: '' !important;
    position: absolute !important;
    width: 100% !important;
    height: 3px !important;
    bottom: -6px !important;
    left: 0 !important;
    background-color: var(--bm-color) !important;
}

blockquote {
    font-size: 0.9em;
    margin: 0 1em 1em 0em;
    max-width: 580px;
    font-family: serif;
    padding: 1.3em 20px 1em 40px;
    border-left: 3px solid var(--accent-2);
    line-height: 1.5;
    position: relative;
    background: hsl(210deg 20% 92%);
    box-sizing: border-box;
    font-family: 'Inter';
    font-size: 0.85em;
}

blockquote::before {
    font-family: 'Inter';
    content: '\201C';
    color: var(--accent-2);
    font-size: 3.8em;
    position: absolute;
    left: 9px;
    top: -10px;
    font-weight: 300;
}

blockquote span {
    display: block;
    color: #000000;
    font-style: normal;
    font-weight: 300;
    margin-top: 1em;
    font-family: 'Inter';
    font-size: 0.9em;
}

.dark blockquote {
    font-size: 0.9em;
    margin: 0 1em 1em 0em;
    max-width: 580px;
    font-family: serif;
    padding: 1.3em 20px 1em 40px;
    line-height: 1.5;
    position: relative;
    background: #1d202a;
    box-sizing: border-box;
    color: #f4f4f4;
    font-family: 'Inter';
    font-size: 0.85em;
}

.dark blockquote::before {
    font-family: 'Inter';
    content: '\201C';
    font-size: 3.8em;
    position: absolute;
    left: 9px;
    top: -10px;
    font-weight: 300;
}

.dark blockquote span {
    display: block;
    color: #cdcdcd;
    font-style: normal;
    font-weight: 300;
    margin-top: 1em;
    font-family: 'Inter';
    font-size: 0.9em;
}
</style>
